import { siteConfig } from "@/config/site";

// SEO helpers — canonical/og:url + stackable JSON-LD builders.
// Relative paths only; hosting resolves against the deployed origin.

type MetaEntry = Record<string, string>;

export type PageSeoInput = {
  path: string; // e.g. "/services/seo"
  title: string;
  description: string;
  image?: string; // absolute URL for og:image (leaf routes only)
  type?: "website" | "article" | "profile";

  keywords?: string[]; // meta keywords — used for internal signal & some engines
  /**
   * Robots directive. Defaults to a permissive "index, follow" with the
   * modern preview hints Google honours. Pass "noindex, nofollow" on
   * pages that should stay out of search.
   */
  robots?: string;
};

const DEFAULT_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

/**
 * Normalize a path so every canonical URL ends with a trailing slash — matches
 * the router's `trailingSlash: "always"` behavior. File-style paths
 * (containing a dot in the final segment, e.g. /sitemap.xml, /llms.txt)
 * and absolute URLs are returned unchanged.
 */
export function withTrailingSlash(path: string): string {
  if (!path) return "/";
  if (/^https?:\/\//i.test(path)) return path;
  const [pathname, ...rest] = path.split(/([?#])/);
  const lastSeg = pathname.split("/").pop() ?? "";
  if (lastSeg.includes(".")) return path;
  const normalized = pathname.endsWith("/") ? pathname : pathname + "/";
  return normalized + rest.join("");
}

// Truncate at a word boundary and append an ellipsis so search engines
// receive a natural-reading snippet instead of a mid-word cut. Titles get
// clamped to ~60 chars, descriptions to ~155 — Google's typical render limits.
function clamp(input: string, max: number): string {
  const s = (input ?? "").trim();
  if (s.length <= max) return s;

  // 1. Try to find the last sentence boundary (period followed by space or end)
  // that fits within the limit.
  const slice = s.slice(0, max);
  const lastSentence = slice.lastIndexOf(". ");

  if (lastSentence > max * 0.7) {
    return slice.slice(0, lastSentence + 1);
  }

  // 2. Fall back to word boundary if no clean sentence break is found
  const wordSlice = s.slice(0, max - 1);
  const cut = wordSlice.lastIndexOf(" ");
  return `${(cut > max * 0.6 ? wordSlice.slice(0, cut) : wordSlice).replace(/[\s.,;:—-]+$/, "")}…`;
}

const MAX_TITLE = 65;
const MAX_DESC = 160;

export function pageMeta({
  path,
  title,
  description,
  image,
  type = "website",
  robots = DEFAULT_ROBOTS,
}: PageSeoInput): {
  meta: MetaEntry[];
  links: MetaEntry[];
} {
  const canonicalPath = withTrailingSlash(path);
  const canonicalUrl = /^https?:\/\//i.test(canonicalPath)
    ? canonicalPath
    : `${siteConfig.origin}${withTrailingSlash(path)}`;
  const safeTitle = clamp(title, MAX_TITLE);
  const safeDescription = clamp(description, MAX_DESC);
  const meta: MetaEntry[] = [

    { title: safeTitle },
    { name: "description", content: safeDescription },
    { name: "robots", content: robots },
    { name: "googlebot", content: robots },
    { property: "og:type", content: type },
    { property: "og:title", content: safeTitle },
    { property: "og:description", content: safeDescription },
    { property: "og:url", content: canonicalUrl },
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: safeTitle },
    { name: "twitter:description", content: safeDescription },
  ];
  if (image) {
    meta.push({ property: "og:image", content: image });
    meta.push({ name: "twitter:image", content: image });
  }
  return {
    meta,
    links: [{ rel: "canonical", href: canonicalUrl }],
  };
}


// ---- JSON-LD builders ------------------------------------------------------

export const SITE_ORIGIN = "https://optimizeplus.net";
/** Stable, absolute @id for the single Organization entity (declared in __root). */
export const ORG_ID = `${SITE_ORIGIN}/#organization`;
export const HQ_LOCALBUSINESS_ID = `${SITE_ORIGIN}/#localbusiness`;
export const SOCIAL_PROFILES = [
  "https://www.facebook.com/optimizplus",
  "https://www.instagram.com/optimize.plus/",
  "https://x.com/optimize_plus",
  "https://www.youtube.com/@OptimizePlus",
  "https://www.linkedin.com/company/optimizplus",
  "https://yelp.com/biz/optimize-plus-austin",
  "https://maps.app.goo.gl/L67bz47dQgtJs4Ba8",
];
const absUrl = (path: string): string =>
  /^https?:\/\//i.test(path) ? path : `${SITE_ORIGIN}${withTrailingSlash(path)}`;

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: siteConfig.name,
    url: siteConfig.origin + "/",
    logo: absUrl(siteConfig.logo.url),

    image: absUrl("/icon-512.png"),
    address: {
      "@type": "PostalAddress",
      streetAddress: siteConfig.contact.address.street,
      addressLocality: siteConfig.contact.address.city,
      addressRegion: siteConfig.contact.address.state,
      postalCode: siteConfig.contact.address.zip,
      addressCountry: siteConfig.contact.address.country,

    },
    description:
      "Austin-based SEO, AEO, and GEO agency helping small businesses and B2B founders across Texas rank on Google, get cited by ChatGPT and Perplexity, and grow qualified organic pipeline.",
    founder: { "@type": "Person", name: "M. Umair Mansha" },
    email: siteConfig.contact.email,
    telephone: siteConfig.contact.phone,

    areaServed: [
      { "@type": "State", name: "Texas" },
      { "@type": "Country", name: "United States" },
    ],
    knowsAbout: [
      "Search Engine Optimization",
      "Answer Engine Optimization",
      "Generative Engine Optimization",
      "Local SEO",
      "Technical SEO",
      "Content Strategy",
      "Link Building",
      "Conversion Rate Optimization",
    ],
    sameAs: SOCIAL_PROFILES,
  };
}

export function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    url: SITE_ORIGIN + "/",
    name: "Optimize Plus",
    publisher: { "@id": ORG_ID },
    inLanguage: "en-US",
  };
}


export type LdCrumb = { name: string; path: string };
export function breadcrumbLd(items: LdCrumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absUrl(c.path),
    })),
  };
}

export function faqLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleLd(input: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    mainEntityOfPage: absUrl(input.path),
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: { "@type": "Person", name: input.authorName ?? "M. Umair Mansha" },
    publisher: {
      "@type": "Organization",
      name: "Optimize Plus",
    },
    ...(input.image
      ? { image: input.image.startsWith("http") ? input.image : absUrl(input.image) }
      : {}),
  };
}


export function serviceLd(input: {
  name: string;
  description: string;
  path: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: input.name,
    description: input.description,
    url: absUrl(input.path),
    serviceType: input.name,
    category: "Search Engine Optimization",
    provider: {
      "@type": "LocalBusiness",
      "@id": HQ_LOCALBUSINESS_ID,
      name: "Optimize Plus",
      parentOrganization: { "@id": ORG_ID },
      url: SITE_ORIGIN + "/",
      sameAs: SOCIAL_PROFILES,
      address: {
        "@type": "PostalAddress",
        streetAddress: siteConfig.contact.address.street,
        addressLocality: siteConfig.contact.address.city,
        addressRegion: siteConfig.contact.address.state,
        postalCode: siteConfig.contact.address.zip,
        addressCountry: siteConfig.contact.address.country,
      },
      telephone: siteConfig.contact.phone,
      email: siteConfig.contact.email,

    },
    areaServed: [
      { "@type": "City", name: "Austin" },
      { "@type": "City", name: "Round Rock" },
      { "@type": "City", name: "Cedar Park" },
      { "@type": "City", name: "San Antonio" },
      { "@type": "City", name: "Houston" },
      { "@type": "City", name: "Dallas" },
      { "@type": "State", name: "Texas" },
      { "@type": "Country", name: "United States" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${input.name} — service options`,
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: input.name },
        },
      ],
    },
  };
}

export function localBusinessLd(input: {
  city: string;
  region?: string; // e.g. "TX"
  path: string; // "/locations/austin/"
  description: string;
  latLng?: { lat: number; lng: number };
  areaServed?: string[]; // nearby city names
  sameAs?: string[];
  kgMid?: string; // Google Knowledge Graph ID
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${absUrl(input.path)}#localbusiness`,
    name: `Optimize Plus — ${input.city}, ${input.region ?? "TX"}`,
    ...(input.kgMid ? {
      identifier: {
        "@type": "PropertyValue",
        propertyID: "googleKgMID",
        value: input.kgMid
      }
    } : {}),
    description: input.description,
    url: absUrl(input.path),
    telephone: siteConfig.contact.phone,
    email: siteConfig.contact.email,

    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: siteConfig.contact.address.street,
      addressLocality: siteConfig.contact.address.city,
      addressRegion: siteConfig.contact.address.state,
      postalCode: siteConfig.contact.address.zip,
      addressCountry: siteConfig.contact.address.country,

    },
    ...(input.latLng
      ? {
          geo: {
            "@type": "GeoCoordinates",
            latitude: input.latLng.lat,
            longitude: input.latLng.lng,
          },
        }
      : {}),
    areaServed: [
      { "@type": "City", name: input.city, containedInPlace: { "@type": "State", name: "Texas" } },
      ...(input.areaServed ?? []).map((n) => ({ "@type": "City", name: n })),
    ],
    image: "https://lh4.googleusercontent.com/-TRH2fCnHTGM/AAAAAAAAAAI/AAAAAAAAAAA/vkoPWqKXXsE/s44-p-k-no-ns-nd/photo.jpg",
    founder: { "@type": "Person", name: "M. Umair Mansha" },
    parentOrganization: { "@id": ORG_ID },
    sameAs: input.sameAs?.length ? input.sameAs : SOCIAL_PROFILES,
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    ],
  };
}





export function blogPostingLd(input: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  image: string;
  keywords?: string[];
  articleSection?: string;
  wordCount?: number;
  faqs?: { q: string; a: string }[];
}) {
  const main: any = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.headline,
    description: input.description,
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(input.path) },
    url: absUrl(input.path),
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    image: [input.image.startsWith("http") ? input.image : absUrl(input.image)],
    author: {
      "@type": "Person",
      name: input.authorName ?? "M. Umair Mansha",
      url: absUrl("/authors/umair-mansha/"),
      "@id": `${SITE_ORIGIN}/authors/umair-mansha/#person`,
    },
    publisher: {
      "@type": "Organization",
      name: siteConfig.name,

      logo: {
        "@type": "ImageObject",
        url: absUrl("/og-image.png"),
      },
    },
    ...(input.keywords?.length ? { keywords: input.keywords.join(", ") } : {}),
    ...(input.articleSection ? { articleSection: input.articleSection } : {}),
    ...(input.wordCount ? { wordCount: input.wordCount } : {}),
    inLanguage: "en-US",
  };

  if (input.faqs?.length) {
    main.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": input.faqs.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    };
  }
  return main;
}

// Convenience: pack JSON-LD objects into head().scripts entries.
export function ldScripts(...objs: unknown[]) {
  return objs.map((o) => ({
    type: "application/ld+json",
    children: JSON.stringify(o),
  }));
}
